import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

// Animation texte hiéroglyphes → texte normal (Labs Agency)
export const HieroglyphText = ({ text, className = "", delay = 0 }) => {
  const [displayText, setDisplayText] = useState('');
  const [isRevealed, setIsRevealed] = useState(false);
  
  const hieroglyphs = '𓀀𓀁𓀂𓀃𓀄𓀅𓀆𓀇𓀈𓀉𓀊𓀋𓀌𓀍𓀎𓀏𓀐𓀑𓀒𓀓𓀔𓀕𓀖𓀗𓀘𓀙𓀚𓀛𓀜𓀝𓀞𓀟';
  
  useEffect(() => {
    const timer = setTimeout(() => {
      let iterations = 0;
      const interval = setInterval(() => {
        setDisplayText(text.split('').map((char, index) => {
          if (index < iterations || char === ' ') {
            return char;
          }
          return hieroglyphs[Math.floor(Math.random() * hieroglyphs.length)];
        }).join(''));
        
        iterations += 0.5; // Plus rapide
        
        if (iterations >= text.length) {
          clearInterval(interval);
          setDisplayText(text);
          setIsRevealed(true);
        }
      }, 30); // Plus rapide
      
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [text, delay]);
  
  return (
    <span className={className}>
      {displayText}
    </span>
  );
};

// Animation texte code → texte normal (Call Up)
export const CodeText = ({ text, className = "", delay = 0 }) => {
  const [displayText, setDisplayText] = useState('');
  const [isRevealed, setIsRevealed] = useState(false);
  
  const codeChars = '01[]{}()<>=+-*/%&|!@#$%^';
  
  useEffect(() => {
    const timer = setTimeout(() => {
      let iterations = 0;
      const interval = setInterval(() => {
        setDisplayText(text.split('').map((char, index) => {
          if (index < iterations || char === ' ') {
            return char;
          }
          return codeChars[Math.floor(Math.random() * codeChars.length)];
        }).join(''));
        
        iterations += 0.5; // Plus rapide
        
        if (iterations >= text.length) {
          clearInterval(interval);
          setDisplayText(text);
          setIsRevealed(true);
        }
      }, 30); // Plus rapide
      
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [text, delay]);
  
  return (
    <span className={className}>
      {displayText}
    </span>
  );
};

// Animation CodeText spéciale pour les chiffres/stats (Call Up)
export const CodeNumber = ({ value, suffix = "", className = "", delay = 0 }) => {
  const [displayText, setDisplayText] = useState('');
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });
  
  const codeChars = '01[]{}()<>=+-*/%&|!@#$%^';
  const fullText = `${value}${suffix}`;
  
  useEffect(() => {
    if (!isInView) return;
    
    const timer = setTimeout(() => {
      let iterations = 0;
      const interval = setInterval(() => {
        setDisplayText(fullText.split('').map((char, index) => {
          if (index < iterations || char === ' ') {
            return char;
          }
          return codeChars[Math.floor(Math.random() * codeChars.length)];
        }).join(''));
        
        iterations += 1.5; // Ultra rapide
        
        if (iterations >= fullText.length) {
          clearInterval(interval);
          setDisplayText(fullText);
        }
      }, 15); // Ultra rapide : 15ms
      
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [isInView, fullText, delay]);
  
  return (
    <span ref={ref} className={className}>
      {displayText || fullText}
    </span>
  );
};

// Formes géométriques en background pour Labs Agency
export const BackgroundShapes = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
      {/* Forme violette avec personnage */}
      <motion.div
        className="absolute"
        style={{
          width: '180px',
          height: '180px',
          top: '15%',
          right: '12%',
        }}
        animate={{
          y: [0, -25, 0],
          rotate: [0, 8, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg viewBox="0 0 200 200" fill="none">
          <motion.path
            d="M50,100 Q50,50 100,50 Q150,50 150,100 Q150,150 100,150 Q50,150 50,100"
            fill="#A855F7"
            animate={{
              d: [
                "M50,100 Q50,50 100,50 Q150,50 150,100 Q150,150 100,150 Q50,150 50,100",
                "M60,100 Q50,60 100,40 Q150,60 140,100 Q150,140 100,160 Q50,140 60,100",
                "M50,100 Q50,50 100,50 Q150,50 150,100 Q150,150 100,150 Q50,150 50,100",
              ],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Personnage souriant */}
          <circle cx="130" cy="85" r="35" fill="#FB923C" />
          <circle cx="120" cy="80" r="4" fill="#000" />
          <circle cx="140" cy="80" r="4" fill="#000" />
          <path d="M115,90 Q130,100 145,90" stroke="#000" strokeWidth="3" fill="none" strokeLinecap="round" />
        </svg>
      </motion.div>

      {/* Forme bleue */}
      <motion.div
        className="absolute"
        style={{
          width: '150px',
          height: '150px',
          bottom: '20%',
          right: '18%',
        }}
        animate={{
          y: [0, 20, 0],
          rotate: [0, -12, 0],
        }}
        transition={{
          duration: 4.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg viewBox="0 0 200 200" fill="none">
          <motion.ellipse
            cx="100"
            cy="100"
            rx="90"
            ry="60"
            fill="#38BDF8"
            animate={{
              rx: [90, 80, 90],
              ry: [60, 70, 60],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </svg>
      </motion.div>

      {/* Forme rose */}
      <motion.div
        className="absolute"
        style={{
          width: '130px',
          height: '130px',
          bottom: '30%',
          right: '35%',
        }}
        animate={{
          y: [0, -18, 0],
          x: [0, 12, 0],
          scale: [1, 1.08, 1],
        }}
        transition={{
          duration: 5.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="80" fill="#EC4899" />
        </svg>
      </motion.div>

      {/* Forme violette carrée */}
      <motion.div
        className="absolute"
        style={{
          width: '120px',
          height: '120px',
          top: '45%',
          right: '8%',
        }}
        animate={{
          rotate: [0, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        <svg viewBox="0 0 200 200" fill="none">
          <rect
            x="50"
            y="50"
            width="100"
            height="100"
            rx="25"
            fill="#9B5DE5"
          />
        </svg>
      </motion.div>

      {/* Points décoratifs */}
      <motion.div
        className="absolute w-4 h-4 rounded-full bg-yellow-400"
        style={{ top: '25%', right: '45%' }}
        animate={{
          scale: [1, 1.6, 1],
          opacity: [1, 0.5, 1],
        }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />
      <motion.div
        className="absolute w-3 h-3 rounded-full bg-orange-400"
        style={{ bottom: '40%', right: '25%' }}
        animate={{
          scale: [1, 1.4, 1],
          opacity: [1, 0.7, 1],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      
      {/* Formes supplémentaires pour plus de densité */}
      <motion.div
        className="absolute"
        style={{
          width: '100px',
          height: '100px',
          top: '60%',
          right: '50%',
        }}
        animate={{
          y: [0, -15, 0],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg viewBox="0 0 200 200" fill="none">
          <polygon points="100,40 160,160 40,160" fill="#F472B6" opacity="0.6" />
        </svg>
      </motion.div>
    </div>
  );
};
